<template>
  <!-- RQ "System Scanner" (Android FragmentSystemScanner): manufacturer / serial / model per unit of each system.
       Android scans with the camera; on the web the numbers are typed. -->
  <div ref="rqScannerModal" class="modal fade" tabindex="-1" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
          <FxModalHeader title="System Scanner" />

          <div class="flex-1 flex flex-col gap-[12px] px-[6px] pt-[12px]">
            <div v-if="editable">
              <FxSelect :model-value="null" :options="systemTypeOptions" placeholder="Add New System" small
                @change="addSystem" />
            </div>

            <div v-for="(sys, si) in systems" :key="si"
              class="flex flex-col gap-[10px] p-[10px] rounded-[8px] border border-[#E1E5EF]">
              <div class="flex items-center justify-between gap-[10px]">
                <span class="text-[16px] font-medium text-[#333333]">{{ capitalize(sys.system_type) }} System</span>
                <button v-if="editable" type="button"
                  class="border-0 bg-transparent p-0 text-[14px] font-medium text-[#EE5851] underline"
                  @click="removeSystem(si)">
                  Remove System
                </button>
              </div>

              <div v-for="(item, ii) in sys.system_items" :key="ii"
                class="flex flex-col gap-[8px] p-[10px] rounded-[8px] bg-[#F7F8FA]">
                <div class="flex items-center justify-between gap-[10px]">
                  <span class="text-[14px] font-medium text-[#7695FF]">{{ item.unit }}</span>
                  <button v-if="editable" type="button"
                    class="border-0 bg-transparent p-0 text-[13px] text-[#EE5851] underline"
                    @click="sys.system_items.splice(ii, 1)">
                    Remove
                  </button>
                </div>
                <div>
                  <span class="uqcLabel">Manufacturer</span>
                  <FxSelect v-model="item.manufacturer" :options="manufacturers" label-key="title" value-key="value"
                    placeholder="Select Manufacturer" :disabled="!editable" inline />
                </div>
                <div>
                  <span class="uqcLabel">Serial Number</span>
                  <inputComponent v-model="item.serial" placeholder="Type serial number" :disabled="!editable"
                    :custom-class="fieldClass" />
                </div>
                <div>
                  <span class="uqcLabel">Model Number</span>
                  <inputComponent v-model="item.model" placeholder="Type model number" :disabled="!editable"
                    :custom-class="fieldClass" />
                </div>
              </div>

              <div v-if="editable">
                <FxSelect :model-value="null" :options="unitOptions(sys)" placeholder="Add Item" small inline
                  @change="addItem(sys, $event)" />
              </div>
            </div>

            <p v-if="!systems.length" class="m-0 text-[14px] text-[#999999] text-center py-[20px]">No systems added</p>
          </div>

          <div class="uqcFooter flex items-center justify-end gap-[10px]">
            <button type="button" class="uqcBtnCancel" data-bs-dismiss="modal">{{ editable ? 'Cancel' : 'Close' }}</button>
            <button v-if="editable" type="button" class="uqcBtnNext" @click="save">Update</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import FxSelect from "../partialComponent/FxSelect.vue";
import inputComponent from "../partialComponent/inputComponent.vue";
import { rqApi } from "@/services/rqApi";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";

const toast = useToast();
const FIRST_UNIT = { package: "Package Unit", split: "Outdoor Unit" };
const UNITS = { package: ["Package Unit", "Thermostat"], split: ["Outdoor Unit", "Indoor Unit", "Thermostat"] };
const blank = (s) => !String(s ?? "").trim();
const fail = (msg) => { toast.error(msg); return false; };

export default {
  components: { FxModalHeader, FxSelect, inputComponent },
  props: {
    orderId: { type: [Number, String], required: true },
    // Android: editing only while order.status < 9
    editable: { type: Boolean, default: false },
    // order.type: the first system offered when nothing is saved yet (Android: split → Split System, else Package)
    orderType: { type: String, default: "" },
  },
  emits: ["changed"],
  data() {
    return {
      systems: [],
      manufacturers: [],
      systemTypeOptions: [{ label: "Package System", value: "package" }, { label: "Split System", value: "split" }],
      fieldClass: "!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333] disabled:!bg-[#F7F8FA] disabled:cursor-not-allowed",
    };
  },
  mounted() {
    this.bsModal = new Modal(this.$refs.rqScannerModal, { backdrop: "static", keyboard: false });
    this.$refs.rqScannerModal.addEventListener("hidden.bs.modal", enableScroll);
  },
  beforeUnmount() {
    this.$refs.rqScannerModal?.removeEventListener("hidden.bs.modal", enableScroll);
    this.bsModal?.dispose();
  },
  methods: {
    async open() {
      const [list, mf] = await Promise.all([
        rqApi.barcodes(this.orderId),
        this.manufacturers.length ? null : rqApi.barcodeManufacturers(),
      ]);
      if (!list.ok) return toast.error(list.message || "Unable to load systems.");
      if (mf?.ok && Array.isArray(mf.body)) this.manufacturers = mf.body;
      const ids = this.manufacturers.map(m => String(m.value));
      // empty list comes back as {}: start with one system of the order's type (Android)
      this.systems = Array.isArray(list.body) && list.body.length
        ? list.body.map(s => ({
          system_type: String(s.system_type || "").toLowerCase(),
          system_items: (s.system_items || []).map(i => ({
            serial: i.serial || "",
            model: i.model || "",
            unit: i.unit,
            // a manufacturer that is not in the picker any more is cleared so validation asks again (Android)
            manufacturer: i.manufacturer != null && ids.includes(String(i.manufacturer)) ? i.manufacturer : null,
          })),
        }))
        : (this.editable ? [this.newSystem((this.orderType || "").toLowerCase() === "split" ? "split" : "package")] : []);
      disableScroll();
      this.bsModal.show();
    },
    capitalize(s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
    },
    newSystem(type) {
      return { system_type: type, system_items: [{ serial: "", model: "", unit: FIRST_UNIT[type], manufacturer: null }] };
    },
    unitOptions(sys) {
      return (UNITS[sys.system_type] || UNITS.package).map(u => ({ label: u, value: u }));
    },
    addSystem(type) {
      if (this.isValid(true)) this.systems.push(this.newSystem(type));
    },
    addItem(sys, unit) {
      if (this.isValid(true)) sys.system_items.push({ serial: "", model: "", unit, manufacturer: null });
    },
    removeSystem(index) {
      this.$dialog.show({
        title: "Alert",
        text: "Are you sure you want to remove this system? It will also remove its items",
        buttonText: "Yes",
        onConfirm: () => this.systems.splice(index, 1),
      });
    },
    // forAdd: only check the items already there before adding a system / item (Android isValid)
    isValid(forAdd) {
      if (!this.systems.length && !forAdd) return fail("Add at least one item");
      const serials = new Set();
      for (const sys of this.systems) {
        if (!sys.system_items.length) {
          if (!forAdd) return fail("Add at least one item");
          continue;
        }
        for (const item of sys.system_items) {
          if (item.manufacturer == null || item.manufacturer === "") return fail("Please select manufacturer.");
          if (blank(item.serial) || blank(item.model)) return fail("All fields are required.");
          const serial = item.serial.trim();
          if (serials.has(serial)) return fail("This serial number already exist, serial number should be unique");
          serials.add(serial);
        }
      }
      return true;
    },
    async save() {
      if (!this.isValid(false)) return;
      const barcode = this.systems.map(s => ({
        system_type: s.system_type,
        system_items: s.system_items.map(i => ({
          serial: i.serial.trim(), model: i.model.trim(), unit: i.unit, manufacturer: i.manufacturer,
        })),
      }));
      const res = await rqApi.saveBarcodes(this.orderId, barcode);
      if (!res.ok) return toast.error(res.message || "Unable to update the systems.");
      toast.success("Updated Successfully");
      this.$emit("changed");
      this.bsModal.hide();
    },
  },
};
</script>
